import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';

const API =
  process.env.REACT_APP_API_URL || '';


// ============================================================
// HELPERS
// ============================================================

function getHeaders() {
  const token =
    localStorage.getItem('adminToken') ||
    '';

  return token
    ? {
        Authorization:
          `Bearer ${token}`,
      }
    : {};
}


function formatDate(value) {
  if (!value) {
    return '—';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '—';
  }

  return date.toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );
}


function formatPeriod(period) {
  const match =
    String(
      period ||
        ''
    ).match(
      /^(\d{4})-(\d{2})$/
    );

  if (!match) {
    return period ||
      'Current month';
  }

  const date =
    new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      1
    );

  return date.toLocaleDateString(
    'en-US',
    {
      month: 'long',
      year: 'numeric',
    }
  );
}


function getPeriodEndLabel(period) {
  const match =
    String(
      period ||
        ''
    ).match(
      /^(\d{4})-(\d{2})$/
    );

  if (!match) {
    return '';
  }

  const year =
    Number(match[1]);

  const month =
    Number(match[2]);

  const end =
    new Date(
      year,
      month,
      0
    );

  return end.toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );
}


function formatMoney(value) {
  const number =
    Number(
      value ||
        0
    );

  return new Intl.NumberFormat(
    'en-US',
    {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }
  ).format(
    Number.isFinite(number)
      ? number
      : 0
  );
}


function getPartnerName(partner) {
  return [
    partner?.firstName,
    partner?.lastName,
  ]
    .filter(Boolean)
    .join(' ')
    .trim() ||
    partner?.email ||
    'Partner';
}


function getMemberName(user) {
  return [
    user?.firstName,
    user?.lastName,
  ]
    .filter(Boolean)
    .join(' ')
    .trim() ||
    user?.name ||
    user?.email ||
    'Unknown member';
}


function getBonusTierLabel(tier) {
  const number =
    Number(
      tier ||
        0
    );

  return number > 0
    ? `${number}+ tier`
    : 'No bonus tier';
}


// ============================================================
// SMALL UI COMPONENTS
// ============================================================

function Pill({
  children,
  tone = 'neutral',
}) {
  const tones = {
    success: {
      background: '#ecfdf5',
      border: '#a7f3d0',
      color: '#065f46',
    },
    warning: {
      background: '#fffbeb',
      border: '#fde68a',
      color: '#92400e',
    },
    danger: {
      background: '#fef2f2',
      border: '#fecaca',
      color: '#991b1b',
    },
    neutral: {
      background: '#f9fafb',
      border: '#e5e7eb',
      color: '#4b5563',
    },
  };

  const selected =
    tones[tone] ||
    tones.neutral;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 9px',
        borderRadius: 999,
        border:
          `1px solid ${selected.border}`,
        background:
          selected.background,
        color:
          selected.color,
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}


function StatusPill({
  status,
}) {
  const active =
    status === 'active';

  return (
    <Pill
      tone={
        active
          ? 'success'
          : 'neutral'
      }
    >
      {active
        ? 'Active'
        : 'Inactive'}
    </Pill>
  );
}


function AccountPill({
  connected,
}) {
  return (
    <Pill
      tone={
        connected
          ? 'success'
          : 'neutral'
      }
    >
      {connected
        ? 'Connected'
        : 'Not connected'}
    </Pill>
  );
}


function RequirementPill({
  complete,
}) {
  return (
    <Pill
      tone={
        complete
          ? 'success'
          : 'neutral'
      }
    >
      {complete
        ? 'Complete'
        : 'Not yet'}
    </Pill>
  );
}


function PaymentPill({
  statement,
}) {
  if (
    statement?.status ===
    'open'
  ) {
    return (
      <Pill tone="warning">
        Open month
      </Pill>
    );
  }

  const paid =
    statement?.paymentStatus ===
    'paid';

  return (
    <Pill
      tone={
        paid
          ? 'success'
          : 'neutral'
      }
    >
      {paid
        ? 'Paid'
        : 'Unpaid'}
    </Pill>
  );
}


function formatAgreementDate(value) {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(new Date(value));
}

function AgreementPill({ status }) {
  if (status?.accepted === true) return <Pill tone="success">Accepted</Pill>;
  if (status?.requiresReacceptance === true) return <Pill tone="warning">Update required</Pill>;
  if (status?.accepted === false) return <Pill>Not accepted</Pill>;
  return <Pill>Unavailable</Pill>;
}

function CreatorAgreementDetails({ partner }) {
  const status = partner?.agreementStatus;
  const accepted = status?.accepted === true;
  const hasPreviousSignature = Boolean(status?.signatureName || status?.acceptedAt || status?.acceptedVersion);
  const history = Array.isArray(partner?.creatorAgreementAcceptances)
    ? [...partner.creatorAgreementAcceptances].sort((a, b) =>
      (new Date(b.acceptedAt).getTime() || 0) - (new Date(a.acceptedAt).getTime() || 0)) : [];
  return <section aria-label="Creator agreement" style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 18, marginBottom: 18 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h3 style={{ fontSize: 14, margin: 0, fontWeight: 800 }}>Creator agreement</h3>
        <AgreementPill status={status} />
      </div>
      {status?.requiredVersion ? <span style={{ fontSize: 12, color: '#6b7280' }}>Current version: {status.requiredVersion}</span> : null}
    </div>
    {hasPreviousSignature ? <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, margin: '14px 0 0' }}>
      <div><dt style={agreementLabelStyle}>{accepted ? 'Signed by' : 'Last signed by'}</dt><dd style={agreementValueStyle}>{status.signatureName || '—'}</dd></div>
      <div><dt style={agreementLabelStyle}>{accepted ? 'Accepted' : 'Last accepted'}</dt><dd style={agreementValueStyle}>{formatAgreementDate(status.acceptedAt)}</dd></div>
      <div><dt style={agreementLabelStyle}>{accepted ? 'Accepted version' : 'Last accepted version'}</dt><dd style={agreementValueStyle}>{status.acceptedVersion || '—'}</dd></div>
    </dl> : null}
    {!accepted ? <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6, margin: '10px 0 0' }}>
      {status?.requiresReacceptance === true
        ? 'They must accept the current agreement before using their creator dashboard or invite code again.'
        : status?.accepted === false
          ? 'Their creator dashboard and invite code stay locked until they accept the agreement.'
          : 'Agreement status is not available. Refresh to check again.'}
    </p> : null}
    {history.length > 0 ? <details style={{ marginTop: 14, fontSize: 12 }}>
      <summary style={{ cursor: 'pointer', color: '#4b5563', fontWeight: 650 }}>Acceptance history ({history.length})</summary>
      <div style={{ overflowX: 'auto', marginTop: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 12 }}>
          <thead><tr><th style={agreementHistoryCellStyle}>Version</th><th style={agreementHistoryCellStyle}>Signed by</th><th style={agreementHistoryCellStyle}>Accepted</th></tr></thead>
          <tbody>{history.map((entry, index) => <tr key={entry.acceptanceKey || `${entry.version}-${entry.acceptedAt}-${index}`}>
            <td style={agreementHistoryCellStyle}>{entry.version || '—'}</td>
            <td style={agreementHistoryCellStyle}>{entry.signatureName || '—'}</td>
            <td style={agreementHistoryCellStyle}>{formatAgreementDate(entry.acceptedAt)}</td>
          </tr>)}</tbody>
        </table>
      </div>
    </details> : null}
  </section>;
}

const agreementLabelStyle = { fontSize: 11, color: '#6b7280', marginBottom: 4 };
const agreementValueStyle = { margin: 0, fontSize: 13, lineHeight: 1.5, color: '#111827', overflowWrap: 'anywhere' };
const agreementHistoryCellStyle = { padding: '8px 10px 8px 0', borderBottom: '1px solid #eeeeea', verticalAlign: 'top' };


function StatCard({
  label,
  value,
  helper = '',
}) {
  return (
    <div
      style={{
        padding: 16,
        border:
          '1px solid #e5e7eb',
        borderRadius: 12,
        background: '#fff',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 850,
          color: '#6b7280',
          textTransform:
            'uppercase',
          letterSpacing:
            '0.02em',
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 25,
          fontWeight: 900,
          color: '#111827',
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: '#9ca3af',
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  );
}


function PartnerIdentity({
  partner,
  size = 38,
}) {
  const name =
    getPartnerName(
      partner
    );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      {partner?.profilePicUrl ? (
        <img
          src={
            partner.profilePicUrl
          }
          alt={name}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background:
              '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              'center',
            fontWeight: 900,
            color: '#6b7280',
            flexShrink: 0,
          }}
        >
          {String(
            partner?.firstName ||
              partner?.email ||
              '?'
          )
            .slice(0, 1)
            .toUpperCase()}
        </div>
      )}

      <div>
        <div
          style={{
            fontWeight: 850,
            color: '#111827',
          }}
        >
          {name}
        </div>

        <div
          style={{
            marginTop: 2,
            fontSize: 12,
            color: '#6b7280',
          }}
        >
          {partner?.email ||
            '—'}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// MAIN COMPONENT
// ============================================================

export default function ReferralPartnersManager() {
  const [
    partners,
    setPartners,
  ] = useState([]);

  const [
    currentPeriod,
    setCurrentPeriod,
  ] = useState('');

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState('');

  const [
    updatingId,
    setUpdatingId,
  ] = useState('');

  const [
    successMessage,
    setSuccessMessage,
  ] = useState('');

  // -------------------------
  // Add Partner
  // -------------------------

  const [
    addOpen,
    setAddOpen,
  ] = useState(false);

  const [
    addSaving,
    setAddSaving,
  ] = useState(false);

  const [
    addError,
    setAddError,
  ] = useState('');

  const [
    addForm,
    setAddForm,
  ] = useState({
    firstName: '',
    lastName: '',
    email: '',
    instagramHandle: '',
    tiktokHandle: '',
    referralCode: '',
  });

  // -------------------------
  // Partner Detail / History
  // -------------------------

  const [
    detailOpen,
    setDetailOpen,
  ] = useState(false);

  const [
    detailPartner,
    setDetailPartner,
  ] = useState(null);

  const [
    detailMode,
    setDetailMode,
  ] = useState(
    'current'
  );

  const [
    statement,
    setStatement,
  ] = useState(null);

  const [
    statementLoading,
    setStatementLoading,
  ] = useState(false);

  const [
    months,
    setMonths,
  ] = useState([]);

  const [
    monthsLoading,
    setMonthsLoading,
  ] = useState(false);

  const [
    allReferrals,
    setAllReferrals,
  ] = useState([]);

  const [participation, setParticipation] = useState(null);

  const [
    allReferralsLoading,
    setAllReferralsLoading,
  ] = useState(false);

  const [
    paymentSaving,
    setPaymentSaving,
  ] = useState(false);

  const [
    paymentNote,
    setPaymentNote,
  ] = useState('');


  // ============================================================
  // API
  // ============================================================

  async function fetchPartners() {
    setLoading(true);
    setError('');

    try {
      const response =
        await axios.get(
          `${API}/referral-partners/admin/partners`,
          {
            headers:
              getHeaders(),
          }
        );

      setPartners(
        Array.isArray(
          response.data?.partners
        )
          ? response.data.partners
          : []
      );

      setCurrentPeriod(
        response.data
          ?.currentPeriod ||
          ''
      );
    } catch (err) {
      console.error(
        '[ReferralPartnersManager fetchPartners]',
        err
      );

      setError(
        err?.response?.data
          ?.message ||
          'Failed to load referral partners.'
      );
    } finally {
      setLoading(false);
    }
  }


  async function fetchMonth(
    partner,
    period
  ) {
    if (
      !partner?._id ||
      !period
    ) {
      return;
    }

    setStatementLoading(true);
    setError('');

    try {
      const response =
        await axios.get(
          `${API}/referral-partners/admin/partners/${partner._id}/months/${period}`,
          {
            headers:
              getHeaders(),
          }
        );

      setDetailPartner(
        response.data
          ?.partner ||
          partner
      );

      const nextStatement =
        response.data
          ?.statement ||
        null;

      setStatement(
        nextStatement
      );

      setPaymentNote(
        String(
          nextStatement
            ?.paymentNote ||
            ''
        )
      );
    } catch (err) {
      console.error(
        '[ReferralPartnersManager fetchMonth]',
        err
      );

      setError(
        err?.response?.data
          ?.message ||
          'Failed to load monthly statement.'
      );
    } finally {
      setStatementLoading(
        false
      );
    }
  }


  async function fetchHistory(
    partner
  ) {
    if (!partner?._id) {
      return;
    }

    setMonthsLoading(true);
    setError('');

    try {
      const response =
        await axios.get(
          `${API}/referral-partners/admin/partners/${partner._id}/months`,
          {
            headers:
              getHeaders(),
          }
        );

      setParticipation(response.data?.participation || null);

      setDetailPartner(
        response.data
          ?.partner ||
          partner
      );

      setMonths(
        Array.isArray(
          response.data?.months
        )
          ? response.data.months
          : []
      );
    } catch (err) {
      console.error(
        '[ReferralPartnersManager fetchHistory]',
        err
      );

      setError(
        err?.response?.data
          ?.message ||
          'Failed to load monthly history.'
      );
    } finally {
      setMonthsLoading(false);
    }
  }


  async function fetchAllReferrals(
    partner
  ) {
    if (!partner?._id) {
      return;
    }

    setAllReferralsLoading(
      true
    );
    setError('');

    try {
      const response =
        await axios.get(
          `${API}/referral-partners/admin/partners/${partner._id}/referrals`,
          {
            headers:
              getHeaders(),
          }
        );

      setParticipation(response.data?.participation || null);

      setDetailPartner(
        response.data
          ?.partner ||
          partner
      );

      setAllReferrals(
        Array.isArray(
          response.data?.referrals
        )
          ? response.data.referrals
          : []
      );
    } catch (err) {
      console.error(
        '[ReferralPartnersManager fetchAllReferrals]',
        err
      );

      setError(
        err?.response?.data
          ?.message ||
          'Failed to load partner referrals.'
      );
    } finally {
      setAllReferralsLoading(
        false
      );
    }
  }


  // ============================================================
  // DETAIL ACTIONS
  // ============================================================

  function resetDetailData() {
    setStatement(null);
    setMonths([]);
    setAllReferrals([]);
    setParticipation(null);
    setPaymentNote('');
  }


  function closeDetail() {
    setDetailOpen(false);
    setDetailPartner(null);
    resetDetailData();
  }


  async function openCurrentMonth(
    partner
  ) {
    setDetailPartner(partner);
    setDetailMode('current');
    setDetailOpen(true);
    resetDetailData();

    await fetchMonth(
      partner,
      currentPeriod
    );
  }


  async function openHistory(
    partner
  ) {
    setDetailPartner(partner);
    setDetailMode('history');
    setDetailOpen(true);
    resetDetailData();

    await fetchHistory(
      partner
    );
  }


  async function openAllReferrals(
    partner
  ) {
    setDetailPartner(partner);
    setDetailMode('all');
    setDetailOpen(true);
    resetDetailData();

    await fetchAllReferrals(
      partner
    );
  }


  async function selectHistoricalMonth(
    month
  ) {
    if (
      !detailPartner ||
      !month?.period
    ) {
      return;
    }

    setDetailMode('month');

    await fetchMonth(
      detailPartner,
      month.period
    );
  }


  async function switchToHistory() {
    if (!detailPartner) {
      return;
    }

    setDetailMode('history');
    setStatement(null);
    await fetchHistory(
      detailPartner
    );
  }


  // ============================================================
  // PAYMENT
  // ============================================================

  async function updatePayment(
    paid
  ) {
    if (
      !detailPartner?._id ||
      !statement?.period
    ) {
      return;
    }

    if (
      paid === false &&
      !window.confirm(
        'Mark this month as unpaid again?'
      )
    ) {
      return;
    }

    setPaymentSaving(true);
    setError('');

    try {
      await axios.patch(
        `${API}/referral-partners/admin/partners/${detailPartner._id}/months/${statement.period}/payment`,
        {
          paid,
          paymentNote:
            paid
              ? paymentNote.trim()
              : '',
        },
        {
          headers:
            getHeaders(),
        }
      );

      await Promise.all([
        fetchMonth(
          detailPartner,
          statement.period
        ),
        fetchPartners(),
      ]);

      setSuccessMessage(
        paid
          ? `${formatPeriod(statement.period)} marked paid.`
          : `${formatPeriod(statement.period)} marked unpaid.`
      );
    } catch (err) {
      console.error(
        '[ReferralPartnersManager updatePayment]',
        err
      );

      setError(
        err?.response?.data
          ?.message ||
          'Failed to update payment status.'
      );
    } finally {
      setPaymentSaving(false);
    }
  }


  // ============================================================
  // ADD PARTNER
  // ============================================================

  function updateAddForm(
    field,
    value
  ) {
    setAddForm(
      (previous) => ({
        ...previous,
        [field]: value,
      })
    );
  }


  function openAddPartner() {
    setAddError('');
    setSuccessMessage('');
    setAddOpen(true);
  }


  function closeAddPartner() {
    if (addSaving) {
      return;
    }

    setAddOpen(false);
    setAddError('');
  }


  async function createPartner(
    event
  ) {
    event.preventDefault();

    const firstName =
      String(
        addForm.firstName ||
          ''
      ).trim();

    const lastName =
      String(
        addForm.lastName ||
          ''
      ).trim();

    const email =
      String(
        addForm.email ||
          ''
      )
        .trim()
        .toLowerCase();

    if (
      !firstName ||
      !lastName ||
      !email
    ) {
      setAddError(
        'First name, last name, and email are required.'
      );

      return;
    }

    setAddSaving(true);
    setAddError('');
    setError('');
    setSuccessMessage('');

    try {
      const response =
        await axios.post(
          `${API}/referral-partners/admin/partners`,
          {
            firstName,
            lastName,
            email,
            instagramHandle:
              String(
                addForm.instagramHandle ||
                  ''
              ).trim(),
            tiktokHandle:
              String(
                addForm.tiktokHandle ||
                  ''
              ).trim(),
            referralCode:
              String(
                addForm.referralCode ||
                  ''
              ).trim(),
          },
          {
            headers:
              getHeaders(),
          }
        );

      const created =
        response.data
          ?.partner;

      setAddForm({
        firstName: '',
        lastName: '',
        email: '',
        instagramHandle: '',
        tiktokHandle: '',
        referralCode: '',
      });

      setAddOpen(false);

      setSuccessMessage(
        created?.accountConnected
          ? `${created.firstName || 'Partner'} was added and connected to their Tribe account.`
          : `${created?.firstName || 'Partner'} was added. No Tribe account is connected yet.`
      );

      await fetchPartners();
    } catch (err) {
      console.error(
        '[ReferralPartnersManager createPartner]',
        err
      );

      setAddError(
        err?.response?.data
          ?.message ||
          'Failed to add referral partner.'
      );
    } finally {
      setAddSaving(false);
    }
  }


  // ============================================================
  // PARTNER STATUS
  // ============================================================

  async function changeStatus(
    partner
  ) {
    const id =
      partner?._id;

    if (!id) {
      return;
    }

    const nextStatus =
      partner.partnerStatus ===
      'active'
        ? 'inactive'
        : 'active';

    setUpdatingId(id);
    setError('');

    try {
      const response =
        await axios.patch(
          `${API}/referral-partners/admin/${id}/partner-status`,
          {
            partnerStatus:
              nextStatus,
          },
          {
            headers:
              getHeaders(),
          }
        );

      const updated =
        response.data
          ?.partner;

      setPartners(
        (previous) =>
          previous.map(
            (item) =>
              String(item._id) ===
              String(id)
                ? {
                    ...item,
                    ...updated,
                  }
                : item
          )
      );

      setDetailPartner(
        (previous) =>
          previous &&
          String(
            previous._id
          ) ===
            String(id)
            ? {
                ...previous,
                ...updated,
              }
            : previous
      );
    } catch (err) {
      console.error(
        '[ReferralPartnersManager changeStatus]',
        err
      );

      setError(
        err?.response?.data
          ?.message ||
          'Failed to update partner status.'
      );
    } finally {
      setUpdatingId('');
    }
  }


  async function copyCode(
    code
  ) {
    if (!code) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        code
      );
    } catch (err) {
      console.error(
        'Unable to copy code:',
        err
      );
    }
  }


  useEffect(() => {
    fetchPartners();
  }, []);


  // ============================================================
  // AGGREGATE STATS
  // ============================================================

  const stats =
    useMemo(
      () =>
        partners.reduce(
          (
            accumulator,
            partner
          ) => {
            accumulator.totalPartners +=
              1;

            if (
              partner.partnerStatus ===
              'active'
            ) {
              accumulator.activePartners +=
                1;
            }

            accumulator.monthlyQualified +=
              Number(
                partner.monthlyQualified ||
                  0
              );

            accumulator.currentEarnings +=
              Number(
                partner.currentEarnings ||
                  0
              );

            accumulator.lifetimeQualified +=
              Number(
                partner.lifetimeQualified ||
                  0
              );

            return accumulator;
          },
          {
            totalPartners: 0,
            activePartners: 0,
            monthlyQualified: 0,
            currentEarnings: 0,
            lifetimeQualified: 0,
          }
        ),
      [partners]
    );


  const thStyle = {
    textAlign: 'left',
    padding: '11px 12px',
    fontSize: 11,
    color: '#6b7280',
    borderBottom:
      '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
    textTransform:
      'uppercase',
    letterSpacing:
      '0.02em',
  };

  const tdStyle = {
    padding: '12px',
    borderBottom:
      '1px solid #f3f4f6',
    fontSize: 13,
    verticalAlign: 'middle',
  };


  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div
      style={{
        maxWidth: 1400,
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 16,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            style={{
              margin: '0 0 4px',
            }}
          >
            Referral Partners
          </h2>

          <div
            style={{
              fontSize: 13,
              color: '#6b7280',
            }}
          >
            {formatPeriod(
              currentPeriod
            )}
            {currentPeriod
              ? ` ends ${getPeriodEndLabel(currentPeriod)}.`
              : ''}
            {' '}
            Track credited signups, bonuses, monthly attendance, and payments.
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            onClick={
              openAddPartner
            }
            style={{
              height: 38,
              padding:
                '0 13px',
              borderRadius: 8,
              border:
                '1px solid #111',
              background: '#111',
              color: '#fff',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            + Add Referral Partner
          </button>

          <button
            type="button"
            onClick={
              fetchPartners
            }
            disabled={loading}
            style={{
              height: 38,
              padding:
                '0 12px',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* MESSAGES */}
      {successMessage ? (
        <div
          style={{
            background:
              '#ecfdf5',
            color: '#065f46',
            border:
              '1px solid #a7f3d0',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {successMessage}
        </div>
      ) : null}

      {error ? (
        <div
          style={{
            background:
              '#fef2f2',
            color: '#991b1b',
            border:
              '1px solid #fecaca',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* STATS */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(5, minmax(150px, 1fr))',
          gap: 12,
          marginBottom: 22,
        }}
      >
        <StatCard
          label="Partners"
          value={
            stats.totalPartners
          }
        />

        <StatCard
          label="Active"
          value={
            stats.activePartners
          }
        />

        <StatCard
          label="Credited This Month"
          value={
            stats.monthlyQualified
          }
        />

        <StatCard
          label="Current Earnings"
          value={
            formatMoney(
              stats.currentEarnings
            )
          }
        />

        <StatCard
          label="Lifetime Credited"
          value={
            stats.lifetimeQualified
          }
        />
      </div>

      {/* PARTNER TABLE */}
      {loading ? (
        <div>
          Loading referral partners...
        </div>
      ) : partners.length ===
        0 ? (
        <div
          style={{
            padding: 20,
            border:
              '1px solid #e5e7eb',
            borderRadius: 10,
            color: '#6b7280',
          }}
        >
          No referral partners yet.
        </div>
      ) : (
        <div
          style={{
            border:
              '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <div
            style={{
              overflowX: 'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                minWidth: 1200,
                borderCollapse:
                  'collapse',
              }}
            >
              <thead>
                <tr
                  style={{
                    background:
                      '#f9fafb',
                  }}
                >
                  <th style={thStyle}>
                    Partner
                  </th>

                  <th style={thStyle}>
                    Code
                  </th>

                  <th style={thStyle}>
                    Signups
                  </th>

                  <th style={thStyle}>
                    Credited
                  </th>

                  <th style={thStyle}>
                    Bonus Tier
                  </th>

                  <th style={thStyle}>
                    Earnings
                  </th>

                  <th style={thStyle}>
                    Monthly Event
                  </th>

                  <th style={thStyle}>
                    Account
                  </th>

                  <th style={thStyle}>
                    Status
                  </th>

                  <th style={thStyle}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {partners.map(
                  (partner) => (
                    <tr
                      key={
                        partner._id
                      }
                    >
                      <td style={tdStyle}>
                        <PartnerIdentity
                          partner={
                            partner
                          }
                        />
                      </td>

                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() =>
                            copyCode(
                              partner.referralCode
                            )
                          }
                          title="Click to copy"
                          style={{
                            fontFamily:
                              'monospace',
                            fontWeight:
                              850,
                            cursor:
                              'pointer',
                            border:
                              '1px solid #e5e7eb',
                            background:
                              '#f9fafb',
                            borderRadius:
                              6,
                            padding:
                              '5px 8px',
                          }}
                        >
                          {partner.referralCode ||
                            '—'}
                        </button>
                      </td>

                      <td style={tdStyle}>
                        {Number(
                          partner.totalSignups ||
                            0
                        )}
                      </td>

                      <td style={tdStyle}>
                        <strong>
                          {Number(
                            partner.monthlyQualified ||
                              0
                          )}
                        </strong>
                      </td>

                      <td style={tdStyle}>
                        {getBonusTierLabel(
                          partner.currentBonusTier
                        )}
                      </td>

                      <td style={tdStyle}>
                        <strong>
                          {formatMoney(
                            partner.currentEarnings
                          )}
                        </strong>

                        {Number(
                          partner.currentBonusAmount ||
                            0
                        ) > 0 ? (
                          <div
                            style={{
                              marginTop: 2,
                              fontSize: 11,
                              color:
                                '#065f46',
                              fontWeight:
                                750,
                            }}
                          >
                            +{formatMoney(
                              partner.currentBonusAmount
                            )} bonus
                          </div>
                        ) : null}
                      </td>

                      <td style={tdStyle}>
                        <RequirementPill
                          complete={
                            partner.monthlyEventAttended
                          }
                        />
                      </td>

                      <td style={tdStyle}>
                        <AccountPill
                          connected={
                            partner.accountConnected
                          }
                        />
                      </td>

                      <td style={tdStyle}>
                        <StatusPill
                          status={
                            partner.partnerStatus
                          }
                        />
                        <div style={{ marginTop: 7 }}>
                          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Agreement</div>
                          <AgreementPill status={partner.agreementStatus} />
                        </div>
                      </td>

                      <td style={tdStyle}>
                        <div
                          style={{
                            display: 'flex',
                            gap: 6,
                            flexWrap:
                              'wrap',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              openCurrentMonth(
                                partner
                              )
                            }
                          >
                            Current Month
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              openHistory(
                                partner
                              )
                            }
                          >
                            History
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              openAllReferrals(
                                partner
                              )
                            }
                          >
                            All Referrals
                          </button>

                          <button
                            type="button"
                            disabled={
                              updatingId ===
                              partner._id
                            }
                            onClick={() =>
                              changeStatus(
                                partner
                              )
                            }
                          >
                            {updatingId ===
                            partner._id
                              ? 'Saving...'
                              : partner.partnerStatus ===
                                'active'
                              ? 'Deactivate'
                              : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ADD PARTNER MODAL */}
      {addOpen ? (
        <div
          onClick={
            closeAddPartner
          }
          style={styles.overlay}
        >
          <form
            onSubmit={
              createPartner
            }
            onClick={(event) =>
              event.stopPropagation()
            }
            style={{
              ...styles.modal,
              maxWidth: 520,
            }}
          >
            <div
              style={
                styles.modalHeader
              }
            >
              <div>
                <div
                  style={
                    styles.modalTitle
                  }
                >
                  Add Referral Partner
                </div>

                <div
                  style={
                    styles.modalSubhead
                  }
                >
                  If the email matches an existing Tribe account or referral partner application, it will be linked automatically.
                </div>
              </div>

              <button
                type="button"
                onClick={
                  closeAddPartner
                }
                disabled={
                  addSaving
                }
              >
                Close
              </button>
            </div>

            {addError ? (
              <div
                style={{
                  ...styles.errorBox,
                  marginBottom: 14,
                }}
              >
                {addError}
              </div>
            ) : null}

            <div
              style={
                styles.formGrid
              }
            >
              <label style={styles.label}>
                First name *
                <input
                  value={
                    addForm.firstName
                  }
                  onChange={(event) =>
                    updateAddForm(
                      'firstName',
                      event.target.value
                    )
                  }
                  style={styles.input}
                />
              </label>

              <label style={styles.label}>
                Last name *
                <input
                  value={
                    addForm.lastName
                  }
                  onChange={(event) =>
                    updateAddForm(
                      'lastName',
                      event.target.value
                    )
                  }
                  style={styles.input}
                />
              </label>
            </div>

            <label style={styles.label}>
              Email *
              <input
                type="email"
                value={
                  addForm.email
                }
                onChange={(event) =>
                  updateAddForm(
                    'email',
                    event.target.value
                  )
                }
                style={styles.input}
              />
            </label>

            <div
              style={
                styles.formGrid
              }
            >
              <label style={styles.label}>
                Instagram
                <input
                  value={
                    addForm.instagramHandle
                  }
                  onChange={(event) =>
                    updateAddForm(
                      'instagramHandle',
                      event.target.value
                    )
                  }
                  style={styles.input}
                />
              </label>

              <label style={styles.label}>
                TikTok
                <input
                  value={
                    addForm.tiktokHandle
                  }
                  onChange={(event) =>
                    updateAddForm(
                      'tiktokHandle',
                      event.target.value
                    )
                  }
                  style={styles.input}
                />
              </label>
            </div>

            <label style={styles.label}>
              Referral code
              <input
                value={
                  addForm.referralCode
                }
                onChange={(event) =>
                  updateAddForm(
                    'referralCode',
                    event.target.value
                  )
                }
                placeholder="Leave blank to generate"
                style={styles.input}
              />
            </label>

            <button
              type="submit"
              disabled={
                addSaving
              }
              style={
                styles.primaryButton
              }
            >
              {addSaving
                ? 'Adding...'
                : 'Add Referral Partner'}
            </button>
          </form>
        </div>
      ) : null}

      {/* DETAIL MODAL */}
      {detailOpen ? (
        <div
          onClick={
            closeDetail
          }
          style={styles.overlay}
        >
          <div
            onClick={(event) =>
              event.stopPropagation()
            }
            style={{
              ...styles.modal,
              maxWidth: 980,
            }}
          >
            <div
              style={
                styles.modalHeader
              }
            >
              <PartnerIdentity
                partner={
                  detailPartner
                }
                size={46}
              />

              <button
                type="button"
                onClick={
                  closeDetail
                }
              >
                Close
              </button>
            </div>

            <CreatorAgreementDetails partner={detailPartner} />

            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 18,
              }}
            >
              <button
                type="button"
                onClick={() =>
                  openCurrentMonth(
                    detailPartner
                  )
                }
                style={
                  detailMode ===
                    'current'
                    ? styles.activeTab
                    : styles.tab
                }
              >
                Current Month
              </button>

              <button
                type="button"
                onClick={
                  switchToHistory
                }
                style={
                  detailMode ===
                    'history'
                    ? styles.activeTab
                    : styles.tab
                }
              >
                Monthly History
              </button>

              <button
                type="button"
                onClick={() =>
                  openAllReferrals(
                    detailPartner
                  )
                }
                style={
                  detailMode ===
                    'all'
                    ? styles.activeTab
                    : styles.tab
                }
              >
                All Referrals
              </button>
            </div>

            {(detailMode ===
              'current' ||
              detailMode ===
                'month') && (
              <MonthStatementView
                statement={
                  statement
                }
                loading={
                  statementLoading
                }
                currentPeriod={
                  currentPeriod
                }
                paymentSaving={
                  paymentSaving
                }
                paymentNote={
                  paymentNote
                }
                setPaymentNote={
                  setPaymentNote
                }
                onMarkPaid={() =>
                  updatePayment(
                    true
                  )
                }
                onUndoPaid={() =>
                  updatePayment(
                    false
                  )
                }
                onBack={
                  detailMode ===
                  'month'
                    ? switchToHistory
                    : null
                }
                thStyle={
                  thStyle
                }
                tdStyle={
                  tdStyle
                }
              />
            )}

            {detailMode ===
              'history' && (
              <HistoryView
                participation={participation}
                months={
                  months
                }
                loading={
                  monthsLoading
                }
                currentPeriod={
                  currentPeriod
                }
                onSelect={
                  selectHistoricalMonth
                }
                thStyle={
                  thStyle
                }
                tdStyle={
                  tdStyle
                }
              />
            )}

            {detailMode ===
              'all' && (
              <AllReferralsView
                participation={participation}
                referrals={
                  allReferrals
                }
                loading={
                  allReferralsLoading
                }
                thStyle={
                  thStyle
                }
                tdStyle={
                  tdStyle
                }
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}


function ParticipationRate({ metrics, countKey, percentageKey }) {
  if (!metrics) return <span style={{ color: '#6b7280' }}>—</span>;
  const count = Number(metrics[countKey] || 0);
  const total = Number(metrics.signupCount || 0);
  return <span style={{ whiteSpace: 'nowrap' }}>
    <strong>{count}</strong> of {total} · {total ? `${Number(metrics[percentageKey] || 0)}%` : '—'}
  </span>;
}

function ParticipationSummary({ metrics, title }) {
  if (!metrics) return null;
  return <section aria-label={title} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 18 }}>
    <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>{title}</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
      <div><div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Signups</div><strong>{Number(metrics.signupCount || 0)}</strong></div>
      <div><div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Booked an event</div><ParticipationRate metrics={metrics} countKey="bookedCount" percentageKey="bookedPercentage" /></div>
      <div><div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Attended</div><ParticipationRate metrics={metrics} countKey="attendedCount" percentageKey="attendedPercentage" /></div>
    </div>
  </section>;
}

function MemberParticipationCell({ happened, date }) {
  if (typeof happened !== 'boolean') return <span style={{ color: '#6b7280' }}>—</span>;
  return <div>
    <Pill tone={happened ? 'success' : 'neutral'}>{happened ? 'Yes' : 'Not yet'}</Pill>
    {happened && date ? <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>{formatDate(date)}</div> : null}
  </div>;
}

const PARTICIPATION_NOTE = 'Grouped by signup month in Los Angeles time. Participation updates as members join events, including in later months. Bookings exclude canceled reservations and no-shows; attendance requires a recorded check-in. These metrics do not change earnings.';

// ============================================================
// MONTH STATEMENT
// ============================================================

function MonthStatementView({
  statement,
  loading,
  currentPeriod,
  paymentSaving,
  paymentNote,
  setPaymentNote,
  onMarkPaid,
  onUndoPaid,
  onBack,
  thStyle,
  tdStyle,
}) {
  if (loading) {
    return (
      <div>
        Loading monthly statement...
      </div>
    );
  }

  if (!statement) {
    return (
      <div
        style={{
          color: '#6b7280',
        }}
      >
        No monthly statement found.
      </div>
    );
  }

  const referrals = Array.isArray(statement.signupReferrals)
    ? statement.signupReferrals
    : Array.isArray(statement.qualifiedReferrals) ? statement.qualifiedReferrals : [];

  const isPaid =
    statement.paymentStatus ===
    'paid';

  const isCurrent =
    statement.period ===
    currentPeriod;

  return (
    <div>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          style={{
            marginBottom: 12,
          }}
        >
          ← Back to History
        </button>
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          gap: 12,
          alignItems:
            'flex-start',
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 900,
            }}
          >
            {formatPeriod(
              statement.period
            )}
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            Ends{' '}
            {getPeriodEndLabel(
              statement.period
            )}
            {isCurrent
              ? ' • Current period'
              : ' • Closed period'}
          </div>
        </div>

        <PaymentPill
          statement={
            statement
          }
        />
      </div>

      <ParticipationSummary metrics={statement.participation} title="Members who signed up this month" />
      {statement.participation ? <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 18px' }}>{PARTICIPATION_NOTE}</p> : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(4, minmax(140px, 1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <StatCard
          label="Credited"
          value={
            Number(
              statement.qualifiedCount ||
                0
            )
          }
        />

        <StatCard
          label="Base"
          value={
            formatMoney(
              statement.baseEarnings
            )
          }
          helper={`${Number(statement.qualifiedCount || 0)} × $${Number(statement.ratePerReferral ?? (statement.programVersion === 'signup-v2' ? 3 : 15))}`}
        />

        <StatCard
          label="Bonus"
          value={
            formatMoney(
              statement.bonusAmount
            )
          }
          helper={
            getBonusTierLabel(
              statement.bonusTier
            )
          }
        />

        <StatCard
          label="Total Earnings"
          value={
            formatMoney(
              statement.totalEarnings
            )
          }
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(2, minmax(220px, 1fr))',
          gap: 10,
          marginBottom: 18,
        }}
      >
        <div
          style={
            styles.infoCard
          }
        >
          <div
            style={
              styles.sectionLabel
            }
          >
            BONUS PROGRESS
          </div>

          <div
            style={{
              fontWeight: 850,
            }}
          >
            {getBonusTierLabel(
              statement.bonusTier
            )}
          </div>

          {statement.nextBonusTier ? (
            <div
              style={{
                marginTop: 5,
                fontSize: 12,
                color: '#6b7280',
              }}
            >
              {Number(
                statement.referralsToNextTier ||
                  0
              )}{' '}
              more credited referral
              {Number(
                statement.referralsToNextTier ||
                  0
              ) === 1
                ? ''
                : 's'}{' '}
              to reach the{' '}
              {statement.nextBonusTier}+
              tier.
            </div>
          ) : (
            <div
              style={{
                marginTop: 5,
                fontSize: 12,
                color: '#065f46',
                fontWeight: 750,
              }}
            >
              Highest bonus tier reached.
            </div>
          )}
        </div>

        <div
          style={
            styles.infoCard
          }
        >
          <div
            style={
              styles.sectionLabel
            }
          >
            MONTHLY EVENT REQUIREMENT
          </div>

          <RequirementPill
            complete={
              Boolean(
                statement.eventAttendedAt
              )
            }
          />

          {statement.eventAttendedAt ? (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: '#6b7280',
              }}
            >
              Attended{' '}
              {formatDate(
                statement.eventAttendedAt
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          ...styles.infoCard,
          marginBottom: 18,
        }}
      >
        <div
          style={
            styles.sectionLabel
          }
        >
          PAYMENT
        </div>

        {statement.status ===
        'open' ? (
          <div
            style={{
              fontSize: 13,
              color: '#6b7280',
            }}
          >
            This month is still open. It can be marked paid after the period closes.
          </div>
        ) : isPaid ? (
          <>
            <div
              style={{
                display: 'flex',
                alignItems:
                  'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <PaymentPill
                statement={
                  statement
                }
              />

              <strong>
                {formatMoney(
                  statement.paidAmount
                )} paid
              </strong>

              <span
                style={{
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                {formatDate(
                  statement.paidAt
                )}
              </span>
            </div>

            {statement.paymentNote ? (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: '#4b5563',
                }}
              >
                {statement.paymentNote}
              </div>
            ) : null}

            {Number(
              statement.paidAmount ||
                0
            ) !==
            Number(
              statement.totalEarnings ||
                0
            ) ? (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: '#92400e',
                  fontWeight: 750,
                }}
              >
                Current calculated earnings are{' '}
                {formatMoney(
                  statement.totalEarnings
                )}, but the recorded payment was{' '}
                {formatMoney(
                  statement.paidAmount
                )}.
              </div>
            ) : null}

            <button
              type="button"
              onClick={
                onUndoPaid
              }
              disabled={
                paymentSaving
              }
              style={{
                marginTop: 10,
              }}
            >
              {paymentSaving
                ? 'Saving...'
                : 'Undo Paid'}
            </button>
          </>
        ) : (
          <>
            <textarea
              value={
                paymentNote
              }
              onChange={(event) =>
                setPaymentNote(
                  event.target.value
                )
              }
              maxLength={500}
              placeholder="Optional payment note"
              style={{
                width: '100%',
                minHeight: 70,
                border:
                  '1px solid #d1d5db',
                borderRadius: 8,
                padding: 9,
                boxSizing:
                  'border-box',
                fontFamily:
                  'inherit',
                resize: 'vertical',
              }}
            />

            <button
              type="button"
              onClick={
                onMarkPaid
              }
              disabled={
                paymentSaving
              }
              style={{
                ...styles.primaryButton,
                marginTop: 8,
              }}
            >
              {paymentSaving
                ? 'Saving...'
                : `Mark ${formatMoney(statement.totalEarnings)} Paid`}
            </button>
          </>
        )}
      </div>

      <div
        style={{
          fontSize: 15,
          fontWeight: 900,
          marginBottom: 8,
        }}
      >
        {statement.participation ? 'Members who signed up this month' : 'Credited Members'}
      </div>

      {referrals.length === 0 ? (
        <div
          style={{
            color: '#6b7280',
            fontSize: 13,
          }}
        >
          No signups in this month.
        </div>
      ) : (
        <div
          style={{
            overflowX: 'auto',
            border:
              '1px solid #e5e7eb',
            borderRadius: 10,
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse:
                'collapse',
            }}
          >
            <thead>
              <tr
                style={{
                  background:
                    '#f9fafb',
                }}
              >
                <th style={thStyle}>
                  Member
                </th>

                <th style={thStyle}>
                  Signed Up
                </th>

                <th style={thStyle}>Booked an event</th>
                <th style={thStyle}>Attended</th>
              </tr>
            </thead>

            <tbody>
              {referrals.map(
                (referral) => {
                  const user =
                    referral.user ||
                    {};

                  return (
                    <tr
                      key={
                        referral._id
                      }
                    >
                      <td style={tdStyle}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems:
                              'center',
                            gap: 8,
                          }}
                        >
                          {user.profilePicUrl ? (
                            <img
                              src={
                                user.profilePicUrl
                              }
                              alt=""
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius:
                                  '50%',
                                objectFit:
                                  'cover',
                              }}
                            />
                          ) : null}

                          <div>
                            <div
                              style={{
                                fontWeight:
                                  800,
                              }}
                            >
                              {getMemberName(
                                user
                              )}
                            </div>

                            <div
                              style={{
                                fontSize:
                                  11,
                                color:
                                  '#6b7280',
                              }}
                            >
                              {user.email ||
                                '—'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td style={tdStyle}>
                        {formatDate(
                          referral.signedUpAt
                        )}
                      </td>

                      <td style={tdStyle}><MemberParticipationCell happened={referral.firstEventBooked} date={referral.firstEventBookedAt} /></td>
                      <td style={tdStyle}><MemberParticipationCell happened={referral.hasAttended} date={referral.attendedAt} /></td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============================================================
// HISTORY
// ============================================================

function HistoryView({
  participation,
  months,
  loading,
  currentPeriod,
  onSelect,
  thStyle,
  tdStyle,
}) {
  if (loading) {
    return (
      <div>
        Loading monthly history...
      </div>
    );
  }

  if (!months.length) {
    return (
      <div
        style={{
          color: '#6b7280',
        }}
      >
        No monthly history yet.
      </div>
    );
  }

  return (
    <div>
      <ParticipationSummary metrics={participation?.allTime} title="All-time member participation" />
      <div
        style={{
          fontSize: 15,
          fontWeight: 900,
          marginBottom: 8,
        }}
      >
        Monthly History
      </div>

      <div
        style={{
          fontSize: 12,
          color: '#6b7280',
          marginBottom: 12,
        }}
      >
        {PARTICIPATION_NOTE}
      </div>

      <div
        style={{
          overflowX: 'auto',
          border:
            '1px solid #e5e7eb',
          borderRadius: 10,
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse:
              'collapse',
          }}
        >
          <thead>
            <tr
              style={{
                background:
                  '#f9fafb',
              }}
            >
              <th style={thStyle}>
                Month
              </th>

              <th style={thStyle}>Signups</th>
              <th style={thStyle}>Booked an event</th>
              <th style={thStyle}>Attended</th>

              <th style={thStyle}>
                Bonus Tier
              </th>

              <th style={thStyle}>
                Earnings
              </th>

              <th style={thStyle}>
                Creator attendance
              </th>

              <th style={thStyle}>
                Payment
              </th>

              <th style={thStyle}>
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {months.map(
              (month) => (
                <tr
                  key={
                    month._id ||
                    month.period
                  }
                >
                  <td style={tdStyle}>
                    <strong>
                      {formatPeriod(
                        month.period
                      )}
                    </strong>

                    {month.period ===
                    currentPeriod ? (
                      <div
                        style={{
                          marginTop: 3,
                        }}
                      >
                        <Pill tone="warning">
                          Current
                        </Pill>
                      </div>
                    ) : null}
                  </td>

                  <td style={tdStyle}>
                    {month.participation ? Number(month.participation.signupCount || 0) : '—'}
                    {month.participation && Number(month.participation.signupCount || 0) !== Number(month.qualifiedCount || 0) ?
                      <div style={{ color: '#6b7280', fontSize: 11 }}>{Number(month.qualifiedCount || 0)} credited for earnings</div> : null}
                  </td>
                  <td style={tdStyle}><ParticipationRate metrics={month.participation} countKey="bookedCount" percentageKey="bookedPercentage" /></td>
                  <td style={tdStyle}><ParticipationRate metrics={month.participation} countKey="attendedCount" percentageKey="attendedPercentage" /></td>

                  <td style={tdStyle}>
                    {getBonusTierLabel(
                      month.bonusTier
                    )}
                  </td>

                  <td style={tdStyle}>
                    <strong>
                      {formatMoney(
                        month.totalEarnings
                      )}
                    </strong>
                  </td>

                  <td style={tdStyle}>
                    <RequirementPill
                      complete={
                        Boolean(
                          month.eventAttendedAt
                        )
                      }
                    />
                  </td>

                  <td style={tdStyle}>
                    <PaymentPill
                      statement={
                        month
                      }
                    />

                    {month.paymentStatus ===
                    'paid' ? (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color:
                            '#6b7280',
                        }}
                      >
                        {formatMoney(
                          month.paidAmount
                        )}
                        {' • '}
                        {formatDate(
                          month.paidAt
                        )}
                      </div>
                    ) : null}
                  </td>

                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() =>
                        onSelect(
                          month
                        )
                      }
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// ALL REFERRALS
// ============================================================

function AllReferralsView({
  participation,
  referrals,
  loading,
  thStyle,
  tdStyle,
}) {
  if (loading) {
    return (
      <div>
        Loading referrals...
      </div>
    );
  }

  if (!referrals.length) {
    return (
      <div
        style={{
          color: '#6b7280',
        }}
      >
        No referred members yet.
      </div>
    );
  }

  return (
    <div>
      <ParticipationSummary metrics={participation?.allTime} title="All-time member participation" />
      <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 12 }}>{PARTICIPATION_NOTE}</p>
    <div
      style={{
        overflowX: 'auto',
        border:
          '1px solid #e5e7eb',
        borderRadius: 10,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse:
            'collapse',
        }}
      >
        <thead>
          <tr
            style={{
              background:
                '#f9fafb',
            }}
          >
            <th style={thStyle}>
              Member
            </th>

            <th style={thStyle}>
              Signed Up
            </th>

            <th style={thStyle}>
              Credited
            </th>

            <th style={thStyle}>Booked an event</th>
            <th style={thStyle}>Attended</th>

            <th style={thStyle}>
              Month
            </th>
          </tr>
        </thead>

        <tbody>
          {referrals.map(
            (referral) => {
              const user =
                referral.user ||
                {};

              const qualified =
                Boolean(
                  (referral.creditedAt || referral.signedUpAt)
                );

              return (
                <tr
                  key={
                    referral._id
                  }
                >
                  <td style={tdStyle}>
                    <div
                      style={{
                        fontWeight:
                          800,
                      }}
                    >
                      {getMemberName(
                        user
                      )}
                    </div>

                    <div
                      style={{
                        fontSize: 11,
                        color:
                          '#6b7280',
                      }}
                    >
                      {user.email ||
                        '—'}
                    </div>
                  </td>

                  <td style={tdStyle}>
                    {formatDate(
                      referral.signedUpAt
                    )}
                  </td>

                  <td style={tdStyle}>
                    <Pill
                      tone={
                        qualified
                          ? 'success'
                          : 'neutral'
                      }
                    >
                      {qualified
                        ? 'Yes'
                        : 'No'}
                    </Pill>
                  </td>

                  <td style={tdStyle}><MemberParticipationCell happened={referral.firstEventBooked} date={referral.firstEventBookedAt} /></td>
                  <td style={tdStyle}><MemberParticipationCell happened={referral.hasAttended} date={referral.attendedAt} /></td>

                  <td style={tdStyle}>
                    {(referral.signupPeriod || referral.creditedPeriod || String(referral.signedUpAt || '').slice(0, 7))
                      ? formatPeriod(
                          (referral.signupPeriod || referral.creditedPeriod || String(referral.signedUpAt || '').slice(0, 7))
                        )
                      : '—'}
                  </td>
                </tr>
              );
            }
          )}
        </tbody>
      </table>
    </div>
    </div>
  );
}


// ============================================================
// STYLES
// ============================================================

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background:
      'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center',
    padding: 20,
    boxSizing:
      'border-box',
  },

  modal: {
    width: '100%',
    maxHeight:
      'calc(100vh - 40px)',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: 14,
    border:
      '1px solid #e5e7eb',
    padding: 20,
    boxSizing:
      'border-box',
  },

  modalHeader: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems:
      'flex-start',
    gap: 12,
    marginBottom: 18,
  },

  modalTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: '#111827',
  },

  modalSubhead: {
    marginTop: 4,
    fontSize: 12,
    color: '#6b7280',
    lineHeight: 1.5,
  },

  formGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },

  label: {
    display: 'block',
    marginBottom: 12,
    fontSize: 12,
    fontWeight: 800,
    color: '#374151',
  },

  input: {
    display: 'block',
    width: '100%',
    height: 40,
    marginTop: 5,
    border:
      '1px solid #d1d5db',
    borderRadius: 8,
    padding:
      '0 10px',
    boxSizing:
      'border-box',
    font: 'inherit',
  },

  primaryButton: {
    minHeight: 38,
    padding:
      '0 13px',
    borderRadius: 8,
    border:
      '1px solid #111',
    background: '#111',
    color: '#fff',
    fontWeight: 800,
    cursor: 'pointer',
  },

  errorBox: {
    background:
      '#fef2f2',
    border:
      '1px solid #fecaca',
    color: '#991b1b',
    padding: 10,
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
  },

  tab: {
    height: 34,
    padding:
      '0 10px',
    borderRadius: 8,
    border:
      '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontWeight: 750,
  },

  activeTab: {
    height: 34,
    padding:
      '0 10px',
    borderRadius: 8,
    border:
      '1px solid #111',
    background: '#111',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 800,
  },

  infoCard: {
    border:
      '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 12,
    background: '#fff',
  },

  sectionLabel: {
    marginBottom: 7,
    fontSize: 11,
    fontWeight: 900,
    color: '#6b7280',
    letterSpacing:
      '0.03em',
  },
};
